<template>
  <div class="card border-0 rounded-0">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <div class="card-header">
        <h3 class="text-black fw-semibold">Détails d'un produit</h3>

        <router-link to="/produits/liste-produits" class="btn btn-primary transition border-0 lh-1 fw-medium"
          style="position: absolute; right: 10px; top: 10px;">
          <i class="flaticon-left-arrow lh-1 me-1"></i>
          Retour
        </router-link>
      </div>
      <div class="card-head box-shadow bg-white d-flex align-items-center justify-content-between"></div>
      <div class="row">
        <!-- Bloc des informations générales -->
        <div class="col-md-8 col-xxl-8 col-sm-12 p-0">
          <h4 class="text-black fw-bold mb-10">Informations générales</h4>
          <table class="table">
            <tbody>
              <tr>
                <td>Code Produit</td>
                <td>{{ produit?.codeProduit }} </td>
              </tr>
              <tr>
                <td>Référence</td>
                <td>{{ produit?.refProd }} </td>
              </tr>
              <tr>
                <td>Famille</td>
                <td>{{ produit?.famille?.libelleFamille }}</td>
              </tr>
              <tr>
                <td>Nom</td>
                <td>{{ produit?.nomProd }}</td>
              </tr>
              <tr v-if="produit?.estModeCarreau">
                <td>Est mode Carreau :</td>
                <td>{{ produit?.estModeCarreau }}</td>
              </tr>
              <tr v-if="produit?.pieceParCarton">
                <td>Pièce par carton :</td>
                <td>{{ produit?.pieceParCarton }}</td>
              </tr>
              <tr v-if="produit?.pieceParMetreCarre">
                <td>Pièce par mètre carré :</td>
                <td>{{ produit?.pieceParMetreCarre }}</td>
              </tr>
              <tr v-if="produit?.metreCarreParCarton">
                <td>Mètre carré par carton :</td>
                <td>{{ produit?.metreCarreParCarton }}</td>
              </tr>
              <tr v-if="produit?.surfaceParPiece">
                <td>Surface par pièce:</td>
                <td>{{ produit?.surfaceParPiece }}</td>
              </tr>
              <tr v-if="produit?.couleur">
                <td>Couleur:</td>
                <td>{{ produit?.couleur }}</td>
              </tr>
              <tr v-if="produit?.uniteGestion">
                <td>Unité de gestion:</td>
                <td>{{ produit?.uniteGestion?.libelle }}</td>
              </tr>
              <tr v-if="produit?.uniteStock">
                <td>Unité de Stock:</td>
                <td>{{ produit?.uniteStock?.libelle }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="produit?.descProd" class="mb-4">
            <h5 class="text-black fw-bold mt-4">Description du produit</h5>
            <p>{{ produit?.descProd }}</p>
          </div>

          <!-- Tableau des conditionnements -->
          <div class="row">
            <div class="col">
              <h5 class="text-black fw-bold mt-4">Conditionnements du produit</h5>
            </div>
            <div class="col">
              <router-link :to="{ name: 'AddProduitConditionnement', params: { id: produit?.id } }"
                class="btn btn-primary mt-3 text-center">
                <i class="fa fa-plus-circle lh-2 me-8 position-relative top-1"></i> Compléter un conditionnement
              </router-link>
            </div>
          </div>
          <table class="table">
            <thead>
              <tr>
                <th>Conditionnement</th>
                <th>Qté Condit.</th>
                <th>UM</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(produitconditionnement, index) in produit?.produitconditionnements" :key="index">
                <td>{{ produitconditionnement.conditionnement?.libelle }}</td>
                <td>{{ produitconditionnement.qteCond }}</td>
                <td>{{ produitconditionnement.conditionnement2?.libelle }}</td>
                <td>
                  <div v-if="index !== 0"> <!-- Ajout de la condition ici -->
                    <span class="">
                      <a class="bullet me-3 btn f-w-500 background-light-danger font-danger" href="javascript:void(0);"
                        @click="suppression(produitconditionnement.id, produit?.produitconditionnements, 'produitconditionnement', `le conditionnement ${produitconditionnement.conditionnement.libelle}`)">
                        <i class="fa fa-trash-o lh-2 me-8 position-relative top-1"></i>
                      </a>
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

        </div>

        <div class="col-md-4 col-xxl-4 col-sm-12 p-0">
          <h5 class="text-black fw-bold mb-10">Image du produit</h5>
          <div class="preview-card">
            <img v-if="produit?.imagePrincipal" :src="imageUrl" alt="Photo du produit" />
            <div v-else class="text-center text-muted">
              <div><i class="icofont icofont-camera fs-1"></i></div>
              Aucune image
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import ApiService from "@/services/ApiService";
import { Produit } from "@/models/Produit";
import { error, getUrlApiForProductFiles, suppression } from "@/utils/utils";
import { useRoute } from "vue-router";
import { computed } from "vue";

export default defineComponent({
  name: "ViewProduit",
  setup() {
    const route = useRoute();
    const produit = ref<Produit | null>(null);
    const medias = ref([]);


    const imageUrl = computed(() =>
      getUrlApiForProductFiles(produit.value?.imagePrincipal ?? null)
    );

    async function getProduit(id: string) {
      try {
        const { data } = await ApiService.get(`/produits/${id}`);
        if (data && data.data) {
          produit.value = data.data;
        } else {
          throw new Error("Données du produit non trouvées.");
        }
      } catch (err) {
        error(err instanceof Error ? err.message : "Erreur lors de la récupération du produit.");
      }
    }

    onMounted(() => {
      const id = route.params.id as string;
      if (id) {
        getProduit(id);
      } else {
        error("ID du produit non spécifié.");
      }
    });

    return {
      produit,
      imageUrl,
      suppression,
      getUrlApiForProductFiles,
    };
  },
});
</script>

<style scoped>
.card {
  border: none;
  /* Retire la bordure */
  margin: 0;
  /* Enlève toutes les marges */
}

.card-body {
  padding: 15px;
  /* Ajustez le padding pour vos besoins */
}

/* Enlève la marge entre les colonnes */
.row {
  margin: 0;
  /* Retire l'espacement de la rangée */
}

/* Ajout d'un petit espacement seulement avec un trait séparateur */
.table {
  border-collapse: collapse;
}

.table td {
  padding: 8px;
}

.preview-card {
  width: 100%;
  height: 260px;
  border: 2px dashed #4caf50;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8f8f8;
}

.preview-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>